/**
 * Servicio para manejar las operaciones de entrevistas RH
 */

import { config, endpoints } from '@/config/env'
import { filterOutMasterUsers } from '@/shared/lib/userFilters'
import { parseDateSafe } from '@/shared/lib/dateUtils'
import type { 
  HRInterview, 
  HRInterviewRegistration, 
  HRInterviewResults, 
  HRInterviewFilters,
  FileUploadResponse,
  PendingHRInterviewResponseDto
} from '@/types/hrInterview'

class HRInterviewService {
  private baseUrl: string

  constructor() {
    this.baseUrl = config.apiBaseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const token = localStorage.getItem('access_token')

    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        let errorData: any = {}
        try {
          const text = await response.text()
          if (text) {
            errorData = JSON.parse(text)
          }
        } catch {
          // Si no se puede parsear como JSON, mantener objeto vacío
        }

        // 🔐 MANEJO DE TOKEN EXPIRADO
        if (response.status === 401) {
          console.error('🚨 [HR Interview Service] TOKEN EXPIRADO - Emitiendo evento de sesión no autorizada')
          
          // Limpiar token
          localStorage.removeItem('access_token')
          
          // Emitir evento para que el hook useAuth lo capture
          window.dispatchEvent(new CustomEvent('auth:unauthorized', {
            detail: { message: errorData.message || 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.' }
          }))
        }

        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }

      // Verificar si hay contenido antes de intentar parsear JSON
      const text = await response.text()
      if (!text) {
        return {} as T
      }
      
      try {
        return JSON.parse(text) as T
      } catch (parseError) {
        console.error('JSON parse error:', parseError, 'Response text:', text)
        throw new Error('Invalid JSON response')
      }
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  /**
   * Registra una nueva entrevista RH
   */
  async registerHRInterview(data: HRInterviewRegistration): Promise<HRInterview> {
    return this.request<HRInterview>('/candidates/hr-interview', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Obtiene todas las entrevistas RH con filtros opcionales
   */
  async getAllHRInterviews(filters?: HRInterviewFilters): Promise<HRInterview[]> {
    let url = '/candidates/hr-interview'
    
    if (filters) {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString())
        }
      })
      
      const queryString = params.toString()
      if (queryString) {
        url += `?${queryString}`
      }
    }
    
    return this.request<HRInterview[]>(url)
  }

  /**
   * Obtiene candidatos pendientes de programar entrevista RH
   * Status: entrevista_rh sin fechaEntrevista programada
   */
  async getPendingSchedule(jobRequestId?: number): Promise<PendingHRInterviewResponseDto[]> {
    try {
      // Intentar usar el nuevo filtro del backend
      const filters: HRInterviewFilters = { status: 'pending_schedule' }
      if (jobRequestId) {
        filters.jobRequestId = jobRequestId
      }

      let url = '/candidates/hr-interview'
      const params = new URLSearchParams()
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString())
        }
      })
      
      const queryString = params.toString()
      if (queryString) {
        url += `?${queryString}`
      }
      
      const result = await this.request<PendingHRInterviewResponseDto[]>(url)
      
      // Si el backend devuelve datos pero son del tipo incorrecto (HRInterview[]),
      // significa que el filtro no está implementado
      if (result && result.length > 0) {
        // Verificar si tiene la estructura correcta
        const firstItem = result[0] as any
        if (firstItem.candidate && firstItem.candidateId) {
          return result
        }
      }
      
      // Si llegamos aquí, el filtro está implementado pero no hay datos
      return result
      
    } catch {
      // Si el backend no soporta el filtro, usar método antiguo
      console.warn('⚠️ Backend no soporta status=pending_schedule, usando filtrado cliente')
      return this.getPendingScheduleFallback(jobRequestId)
    }
  }

  /**
   * Fallback: Obtiene candidatos pendientes buscando directamente en el sistema
   * Esto es necesario porque los candidatos en pre_registro no tienen entrevistas RH aún
   */
  private async getPendingScheduleFallback(jobRequestId?: number): Promise<PendingHRInterviewResponseDto[]> {
    try {
      // Obtener todas las entrevistas RH para candidatos con entrevista_rh
      const allInterviews = await this.getAllHRInterviews()
      
      // Filtrar entrevistas sin fecha (solo para entrevista_rh)
      const pendingFromInterviews = allInterviews.filter(interview => 
        !interview.fechaEntrevista && 
        interview.candidate.status === 'entrevista_rh' &&
        (!jobRequestId || interview.candidate.jobRequest?.id === jobRequestId)
      ).map(interview => ({
        candidateId: interview.candidateId,
        candidate: {
          id: interview.candidate.id,
          fullName: interview.candidate.fullName,
          email: interview.candidate.email,
          phone: interview.candidate.phone,
          age: interview.candidate.age,
          experienceYears: interview.candidate.experienceYears,
          experienceDescription: interview.candidate.experienceDescription,
          status: interview.candidate.status as 'entrevista_rh' | 'pre_registro',
          jobRequest: {
            id: interview.candidate.jobRequest?.id || 0,
            puestoSolicitar: interview.candidate.jobRequest?.puestoSolicitar || '',
            areaVacante: interview.candidate.jobRequest?.areaVacante || '',
            sede: interview.candidate.jobRequest?.sede || ''
          }
        },
        interviewExists: true,
        createdAt: interview.createdAt
      }))

      // Para candidatos en pre_registro, necesitamos obtenerlos del endpoint de candidatos
      // Ya que no tienen entrevistas RH creadas aún
      const candidatesInPreRegistro = await this.getCandidatesInPreRegistro(jobRequestId)

      // Combinar ambos resultados
      const allPending = [...pendingFromInterviews, ...candidatesInPreRegistro]

      return allPending
    } catch (error) {
      console.error('Error en fallback:', error)
      return []
    }
  }

  /**
   * Obtiene candidatos en status pre_registro que aún no tienen entrevista RH
   */
  private async getCandidatesInPreRegistro(jobRequestId?: number): Promise<PendingHRInterviewResponseDto[]> {
    try {
      // Obtener vacantes activas desde el API
      const response = await fetch(`${this.baseUrl}/job-requests/approved`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      })

      if (!response.ok) {
        console.warn('No se pudieron obtener vacantes activas')
        return []
      }

      const activeJobs = await response.json()
      
      // Obtener candidatos de todas las vacantes (o de una específica)
      const jobsToFetch = jobRequestId 
        ? activeJobs.filter((job: any) => job.id === jobRequestId)
        : activeJobs

      const candidatesPromises = jobsToFetch.map(async (job: any) => {
        try {
          const candidatesResponse = await fetch(
            `${this.baseUrl}/candidates/by-job-request/${job.id}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            }
          )

          if (!candidatesResponse.ok) return []

          const candidates = await candidatesResponse.json()
          
          // Filtrar solo candidatos en pre_registro
          return candidates
            .filter((c: any) => c.status === 'pre_registro')
            .map((candidate: any) => ({
              candidateId: candidate.id,
              candidate: {
                id: candidate.id,
                fullName: candidate.fullName,
                email: candidate.email,
                phone: candidate.phone || '',
                age: candidate.age,
                experienceYears: candidate.experienceYears,
                experienceDescription: candidate.experienceDescription,
                status: 'pre_registro' as const,
                jobRequest: {
                  id: job.id,
                  puestoSolicitar: job.puestoSolicitar,
                  areaVacante: job.areaVacante,
                  sede: job.sede
                }
              },
              interviewExists: false,
              createdAt: candidate.createdAt
            }))
        } catch (error) {
          console.error(`Error obteniendo candidatos del job ${job.id}:`, error)
          return []
        }
      })

      const candidatesArrays = await Promise.all(candidatesPromises)
      const allCandidates = candidatesArrays.flat()

      return allCandidates
    } catch (error) {
      console.error('Error obteniendo candidatos en pre_registro:', error)
      return []
    }
  }

  /**
   * Obtiene entrevistas programadas (con fecha pero sin decisión)
   * Status: scheduled (decision = 'pendiente')
   */
  async getScheduledInterviews(jobRequestId?: number): Promise<HRInterview[]> {
    try {
      const filters: HRInterviewFilters = { status: 'scheduled' }
      if (jobRequestId) {
        filters.jobRequestId = jobRequestId
      }

      let url = '/candidates/hr-interview'
      const params = new URLSearchParams()
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString())
        }
      })
      
      const queryString = params.toString()
      if (queryString) {
        url += `?${queryString}`
      }
      
      return await this.request<HRInterview[]>(url)
    } catch {
      // Fallback: filtrar en cliente
      console.warn('⚠️ Backend no soporta status=scheduled, usando filtrado cliente')
      const allInterviews = await this.getAllHRInterviews()
      return allInterviews.filter(interview => 
        interview.fechaEntrevista && 
        (!interview.decision || interview.decision === 'pendiente') &&
        (!jobRequestId || interview.candidate.jobRequest?.id === jobRequestId)
      )
    }
  }

  /**
   * Obtiene entrevistas completadas (con decisión tomada)
   * Status: completed (decision = 'aprobado' o 'rechazado')
   */
  async getCompletedInterviews(jobRequestId?: number, decision?: 'aprobado' | 'rechazado'): Promise<HRInterview[]> {
    try {
      const filters: HRInterviewFilters = { status: 'completed' }
      if (jobRequestId) {
        filters.jobRequestId = jobRequestId
      }
      if (decision) {
        filters.decision = decision
      }

      let url = '/candidates/hr-interview'
      const params = new URLSearchParams()
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString())
        }
      })
      
      const queryString = params.toString()
      if (queryString) {
        url += `?${queryString}`
      }
      
      return await this.request<HRInterview[]>(url)
    } catch {
      // Fallback: filtrar en cliente
      console.warn('⚠️ Backend no soporta status=completed, usando filtrado cliente')
      const allInterviews = await this.getAllHRInterviews()
      return allInterviews.filter(interview => 
        interview.decision && 
        (interview.decision === 'aprobado' || interview.decision === 'rechazado') &&
        (!jobRequestId || interview.candidate.jobRequest?.id === jobRequestId) &&
        (!decision || interview.decision === decision)
      )
    }
  }

  /**
   * Obtiene una entrevista RH específica por ID
   */
  async getHRInterviewById(id: number): Promise<HRInterview> {
    return this.request<HRInterview>(`/candidates/hr-interview/${id}`)
  }

  /**
   * Obtiene la entrevista RH de un candidato específico
   */
  async getHRInterviewByCandidate(candidateId: string): Promise<HRInterview | null> {
    try {
      const result = await this.request<HRInterview>(`/candidates/hr-interview/candidate/${candidateId}`)
      
      // Si el resultado está vacío o es un objeto vacío, retornamos null
      if (!result || (typeof result === 'object' && Object.keys(result).length === 0)) {
        return null
      }
      
      return result
    } catch (error) {
      // Si no existe entrevista para el candidato, retornamos null
      if (error instanceof Error && (
        error.message.includes('404') || 
        error.message.includes('Invalid JSON response') ||
        error.message.includes('Unexpected end of JSON input')
      )) {
        return null
      }
      throw error
    }
  }

  /**
   * Carga los resultados de una entrevista RH
   */
  async uploadHRInterviewResults(
    interviewId: number, 
    results: HRInterviewResults
  ): Promise<HRInterview> {
    return this.request<HRInterview>(`/candidates/hr-interview/${interviewId}/results`, {
      method: 'PATCH',
      body: JSON.stringify(results),
    })
  }

  /**
   * Sube el archivo de test psicométrico/técnico
   */
  async uploadTestFile(interviewId: number, file: File): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', 'psychometric-tests') // Carpeta específica para tests

    const token = localStorage.getItem('access_token')
    const headers: HeadersInit = {}
    
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    try {
      // 1. Subir archivo a S3
      const uploadResponse = await fetch(
        `${this.baseUrl}/upload/s3`,
        {
          method: 'POST',
          headers,
          body: formData,
        }
      )

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}))
        throw new Error(errorData.message || `Error uploading file: ${uploadResponse.status}`)
      }

      const uploadResult = await uploadResponse.json()
      
      if (!uploadResult.url) {
        throw new Error('No se recibió la URL del archivo')
      }

      // 2. Actualizar la entrevista con la URL del archivo
      const updateResponse = await fetch(
        `${this.baseUrl}/candidates/hr-interview/${interviewId}`,
        {
          method: 'PATCH',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            archivoPsicometricoTecnico: uploadResult.url
          }),
        }
      )

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({}))
        throw new Error(errorData.message || `Error updating interview: ${updateResponse.status}`)
      }

      return {
        success: true,
        fileUrl: uploadResult.url,
        message: 'Archivo subido exitosamente'
      }
    } catch (error) {
      console.error('File upload failed:', error)
      throw error
    }
  }

  /**
   * Elimina una entrevista RH (solo si no tiene resultados)
   */
  async deleteHRInterview(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/candidates/hr-interview/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Verifica si un candidato puede tener una entrevista RH programada
   */
  async canScheduleHRInterview(candidateId: string): Promise<{ canSchedule: boolean; reason?: string }> {
    try {
      // Verificar si ya existe una entrevista
      const existingInterview = await this.getHRInterviewByCandidate(candidateId)
      
      if (existingInterview) {
        return {
          canSchedule: false,
          reason: 'El candidato ya tiene una entrevista RH programada'
        }
      }

      return { canSchedule: true }
    } catch (error) {
      console.error('Error checking HR interview eligibility:', error)
      return {
        canSchedule: false,
        reason: 'Error al verificar elegibilidad para entrevista RH'
      }
    }
  }

  /**
   * Obtiene los entrevistadores disponibles (usuarios con rol RH)
   */
  async getAvailableInterviewers(): Promise<Array<{
    id: string
    firstName: string
    lastName: string
    email: string
    role: string
  }>> {
    const interviewers = await this.request<Array<{
      id: string
      firstName: string
      lastName: string
      email: string
      role: string
    }>>(endpoints.hrInterviews.interviewers)
    return filterOutMasterUsers(interviewers)
  }

  /**
   * Valida los datos de registro de entrevista
   */
  validateInterviewRegistration(data: HRInterviewRegistration): { isValid: boolean; errors: string[] } {
    const errors: string[] = []

    if (!data.candidateId) {
      errors.push('ID del candidato es requerido')
    }

    if (!data.fechaEntrevista) {
      errors.push('Fecha de entrevista es requerida')
    } else {
      const fecha = parseDateSafe(data.fechaEntrevista)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      if (fecha && fecha < today) {
        errors.push('La fecha de entrevista no puede ser en el pasado')
      }
    }

    if (!data.horaEntrevista) {
      errors.push('Hora de entrevista es requerida')
    } else {
      const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/
      if (!timeRegex.test(data.horaEntrevista)) {
        errors.push('Formato de hora inválido (HH:MM)')
      }
    }

    if (!data.modalidad || !['presencial', 'virtual'].includes(data.modalidad)) {
      errors.push('Modalidad debe ser "presencial" o "virtual"')
    }

    if (!data.ubicacionLink) {
      errors.push('Ubicación/Link es requerido')
    }

    if (!data.entrevistadorId) {
      errors.push('Entrevistador es requerido')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * Valida los resultados de entrevista
   */
  validateInterviewResults(data: HRInterviewResults): { isValid: boolean; errors: string[] } {
    const errors: string[] = []

    if (data.resultadoEntrevistaRh < 1 || data.resultadoEntrevistaRh > 10) {
      errors.push('El resultado de entrevista debe estar entre 1 y 10')
    }

    if (!data.comentariosEntrevistaRh || data.comentariosEntrevistaRh.trim().length < 10) {
      errors.push('Los comentarios de entrevista son requeridos (mínimo 10 caracteres)')
    }

    if (data.decision === 'rechazado' && (!data.motivoRechazo || data.motivoRechazo.trim().length < 5)) {
      errors.push('El motivo de rechazo es requerido cuando la decisión es "rechazado"')
    }

    // Sin decision no se manda. El formulario abre vacio a proposito
    // (SRH-307): antes traia «pendiente» puesta y se guardaba sin que nadie
    // hubiera decidido, dejando al candidato fuera de la etapa siguiente.
    if (!data.decision) {
      errors.push('Elige la decisión de la entrevista')
    } else if (!['aprobado', 'rechazado', 'pendiente'].includes(data.decision)) {
      errors.push('Decisión debe ser "aprobado", "rechazado" o "pendiente"')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }
}

// Export singleton instance
export const hrInterviewService = new HRInterviewService()
